/**
 * Handover presence service — the ephemeral proof-of-presence code.
 *
 * Class-based singleton per architecture rules. The mint edge acuña un código
 * de 6 caracteres (5 min, un solo uso, solo su hash se guarda); el verify edge
 * lo consume. Si la inspeccion ya existe la marca en ese instante y, si todavia
 * se esta llenando, la migracion la enlaza cuando la fila se persiste. Evidencia
 * adicional, nunca una compuerta del traspaso.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

interface MintResult {
  code: string
  expiresAt: string
}

class HandoverPresenceServiceImpl {
  async mintToken(rentalId: string, inspectionType: 'pickup' | 'dropoff'): Promise<MintResult> {
    const { data, error } = await supabase.functions.invoke('mint-handover-token', {
      body: { rentalId, inspectionType },
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    const result = data as { code?: string; expiresAt?: string; error?: string }
    if (!result?.code || !result?.expiresAt) {
      throw new ServiceError({
        message: result?.error ?? 'No se pudo acuñar el código de presencia.',
        code: 'MINT_FAILED',
        statusCode: 422,
      })
    }
    return { code: result.code, expiresAt: result.expiresAt }
  }

  async verifyCode(
    code: string,
    rentalId: string,
    inspectionType: 'pickup' | 'dropoff',
  ): Promise<void> {
    const { data, error } = await supabase.functions.invoke('verify-handover-presence', {
      body: { rentalId, inspectionType, code },
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    const result = data as { verified?: boolean; error?: string }
    if (!result?.verified) {
      throw new ServiceError({
        message: result?.error ?? 'El código no coincide.',
        code: 'VERIFY_FAILED',
        statusCode: 422,
      })
    }
  }

  /**
   * ¿Ya quedó probada la presencia? El RPC es deliberadamente la única lectura
   * de los tokens: autentica al participante, no revela ningún código y también
   * reconcilia un código verificado antes de que la inspección se guardara.
   */
  async getPresenceStatus(
    rentalId: string,
    inspectionType: 'pickup' | 'dropoff',
  ): Promise<boolean> {
    const { data, error } = await supabase.rpc('handover_presence_status', {
      p_rental_id: rentalId,
      p_inspection_type: inspectionType,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    return data === true
  }
}

export const handoverPresenceService = new HandoverPresenceServiceImpl()
